/**
 * THE MOISTURE DETERMINATION — one container weighed wet and dry.
 *
 * A reusable domain component (owner specification of 2026-10-17, section 6):
 * ASTM D2216 is its first consumer, and the same block appears inside D1557
 * and D4318, which are NOT implemented here and stay `not_ready`.
 *
 * Every formula is traced to the laboratory's validated workbook
 * (`SOIL-ASTM D2216 Rev.01.xlsx`, sheet `Moisture`, decision D237):
 *
 *   Mw = Mcms - Mcds            (cell F24 `=F22-F23`)
 *   Ms = Mcds - Mc              (cell F25 `=F23-F21`)
 *   w% = Mw / Ms x 100          (cell F26 `=F24/F25*100`)
 *
 * EXACT DECIMAL ARITHMETIC (D241). Readings arrive as decimal TEXT and are
 * held as scaled integers, so Mw and Ms are exact and nothing drifts the way
 * `478.6 - 470.21` does in floating point. Only w% involves a division; it is
 * carried to `WATER_CONTENT_SCALE` places, rounded half away from zero at the
 * last place. That is storage precision, not a rounding rule: what a person
 * SEES is shaped by `formatDecimalForDisplay` with the workbook's own number
 * formats (`MOISTURE_DISPLAY_PLACES`).
 *
 * A reading is never corrected. An incomplete or impossible determination is
 * reported, with the exact field and a stable code, and is not calculated.
 */
/** Decimal places a reading may carry. */
export const READING_SCALE = 4;
/** Integer digits a reading may carry. */
export const READING_INTEGER_DIGITS = 7;
/** Decimal places the stored water content is carried to (D241). */
export const WATER_CONTENT_SCALE = 10;
/**
 * DISPLAY precision, from the workbook's number formats: masses `0.00`
 * (F21:F25), water content `0.0` (F26). Display only — never storage.
 */
export const MOISTURE_DISPLAY_PLACES = Object.freeze({
    mass: 2,
    waterContent: 1,
});
/** The three readings, in the order the workbook lists them (rows 21–23). */
export const MOISTURE_READING_FIELDS = Object.freeze([
    'containerMass',
    'containerWetMass',
    'containerDryMass',
]);
export const MOISTURE_ISSUE_CODE = Object.freeze({
    /** A reading is missing. Never treated as zero (CLAUDE.md section 8). */
    READING_REQUIRED: 'reading_required',
    /** Not a plain decimal number of the accepted size. */
    NOT_A_DECIMAL: 'not_a_decimal',
    /** A mass below zero. */
    NEGATIVE_MASS: 'negative_mass',
    /** Mcms < Mcds: the water mass would be negative. */
    WET_BELOW_DRY: 'wet_below_dry',
    /** Mcds <= Mc: the dry soil mass, the denominator, would be zero or negative. */
    DRY_NOT_ABOVE_CONTAINER: 'dry_not_above_container',
});
export const MOISTURE_DETERMINATION_STATUS = Object.freeze({
    /** All readings present, numeric and physically possible: calculated. */
    COMPLETE: 'complete',
    /** At least one reading missing, none wrong: not calculated. */
    INCOMPLETE: 'incomplete',
    /** At least one reading wrong or impossible: not calculated. */
    INVALID: 'invalid',
});
const DECIMAL_PATTERN = new RegExp(`^(-?)(\\d{1,${READING_INTEGER_DIGITS}})(?:\\.(\\d{1,${READING_SCALE}}))?$`);
export function parseReading(text) {
    if (text === null || text === undefined)
        return { kind: 'missing' };
    const trimmed = text.trim();
    if (trimmed === '')
        return { kind: 'missing' };
    const match = DECIMAL_PATTERN.exec(trimmed);
    if (!match)
        return { kind: 'invalid' };
    const [, sign, whole, fraction = ''] = match;
    const scaled = BigInt(`${whole}${fraction.padEnd(READING_SCALE, '0')}`);
    return { kind: 'value', scaled: sign === '-' ? -scaled : scaled };
}
/** Is this text a decimal number of the accepted size (sign allowed)? */
export function isDecimalReading(text) {
    return parseReading(text).kind === 'value';
}
/** A scaled integer as plain decimal text with exactly `scale` places. */
export function scaledToDecimal(value, scale) {
    const negative = value < 0n;
    const digits = (negative ? -value : value).toString().padStart(scale + 1, '0');
    const whole = digits.slice(0, digits.length - scale);
    const fraction = scale > 0 ? `.${digits.slice(digits.length - scale)}` : '';
    return `${negative ? '-' : ''}${whole}${fraction}`;
}
/** numerator / denominator, rounded half away from zero to an integer. */
function divideHalfAwayFromZero(numerator, denominator) {
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = (2n * n + d) / (2n * d);
    return negative ? -quotient : quotient;
}
/**
 * Evaluate one determination. Pure: the same function runs on the server,
 * whose answer is stored, and in the browser, whose answer is a preview.
 */
export function evaluateMoistureDetermination(readings) {
    const issues = [];
    const values = new Map();
    let missing = false;
    for (const field of MOISTURE_READING_FIELDS) {
        const parsed = parseReading(readings[field]);
        if (parsed.kind === 'missing') {
            missing = true;
            issues.push({ field, code: MOISTURE_ISSUE_CODE.READING_REQUIRED });
        }
        else if (parsed.kind === 'invalid') {
            issues.push({ field, code: MOISTURE_ISSUE_CODE.NOT_A_DECIMAL });
        }
        else if (parsed.scaled < 0n) {
            issues.push({ field, code: MOISTURE_ISSUE_CODE.NEGATIVE_MASS });
        }
        else {
            values.set(field, parsed.scaled);
        }
    }
    const mc = values.get('containerMass');
    const mcms = values.get('containerWetMass');
    const mcds = values.get('containerDryMass');
    // The relationships are checked whenever the readings they compare exist,
    // so an impossible pair is named even while a third reading is missing.
    if (mcms !== undefined && mcds !== undefined && mcms < mcds) {
        issues.push({ field: 'containerWetMass', code: MOISTURE_ISSUE_CODE.WET_BELOW_DRY });
    }
    if (mcds !== undefined && mc !== undefined && mcds <= mc) {
        issues.push({ field: 'containerDryMass', code: MOISTURE_ISSUE_CODE.DRY_NOT_ABOVE_CONTAINER });
    }
    const wrong = issues.some((issue) => issue.code !== MOISTURE_ISSUE_CODE.READING_REQUIRED);
    if (wrong) {
        return { status: MOISTURE_DETERMINATION_STATUS.INVALID, issues, calculation: null };
    }
    if (missing || mc === undefined || mcms === undefined || mcds === undefined) {
        return { status: MOISTURE_DETERMINATION_STATUS.INCOMPLETE, issues, calculation: null };
    }
    const waterMass = mcms - mcds;
    const drySoilMass = mcds - mc;
    // Both masses carry READING_SCALE places, which cancel in the ratio.
    const waterContent = divideHalfAwayFromZero(waterMass * 100n * 10n ** BigInt(WATER_CONTENT_SCALE), drySoilMass);
    return {
        status: MOISTURE_DETERMINATION_STATUS.COMPLETE,
        issues: [],
        calculation: {
            waterMass: scaledToDecimal(waterMass, READING_SCALE),
            drySoilMass: scaledToDecimal(drySoilMass, READING_SCALE),
            waterContentPercent: scaledToDecimal(waterContent, WATER_CONTENT_SCALE),
        },
    };
}
/**
 * Decimal text shaped to `places` for DISPLAY, half away from zero — how the
 * workbook's number formats show a value. Never used to store anything.
 * Returns null for text that is not a decimal number.
 */
export function formatDecimalForDisplay(text, places) {
    if (text === null || text === undefined)
        return null;
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text.trim());
    if (!match)
        return null;
    const [, sign, whole, fraction = ''] = match;
    const scale = Math.max(fraction.length, places);
    const scaled = BigInt(`${whole}${fraction.padEnd(scale, '0')}`);
    const rounded = divideHalfAwayFromZero(scaled, 10n ** BigInt(scale - places));
    const isZero = rounded === 0n;
    return scaledToDecimal(sign === '-' && !isZero ? -rounded : rounded, places);
}
//# sourceMappingURL=moisture-determination.js.map